import { Button } from "@/components/ui/button"
import { Spinner } from "@/components/ui/spinner"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import type { BillingPlanId } from "@/services/api/billingService"
import { CircleCheck, Clock } from "lucide-react"
import { Link } from "react-router"
import { PLAN_COPY } from "./planCopy"
import { UpgradeState } from "./UpgradeStates"
import type { PaidAccessState } from "./useAwaitPaidAccess"

interface CheckoutSuccessCardProps {
  /** The bought plan, when known. */
  plan?: BillingPlanId
  /** waiting: paid, activation pending; ready: plan active; timeout: activation is slow. */
  state: PaidAccessState
  onRetry: () => void
}

const DESCRIPTION = {
  waiting: "UPGRADE/SUCCESS_ACTIVATING",
  ready: "UPGRADE/SUCCESS_DESCRIPTION",
  timeout: "UPGRADE/ACTIVATION_SLOW",
} as const

const ICON = {
  waiting: <Spinner className="size-6" />,
  ready: <CircleCheck className="size-6 text-success" aria-hidden="true" />,
  timeout: <Clock className="size-6 text-warning" aria-hidden="true" />,
}

/** One page from payment to access: the icon, copy and actions change in place. */
export const CheckoutSuccessCard: React.FC<CheckoutSuccessCardProps> = ({
  plan,
  state,
  onRetry,
}) => {
  const { translate } = useTranslation()
  return (
    <UpgradeState
      role="status"
      dataTestId="upgrade-success"
      icon={ICON[state]}
      title={
        state === "ready" && plan
          ? translate("UPGRADE/WELCOME", {
              plan: translate(PLAN_COPY[plan].name),
            })
          : translate("UPGRADE/SUCCESS_TITLE")
      }
      description={translate(DESCRIPTION[state])}
      actions={
        state !== "waiting" && (
          <>
            {state === "timeout" && (
              <Button variant="outline" onClick={onRetry}>
                {translate("UPGRADE/RETRY")}
              </Button>
            )}
            <Button asChild>
              <Link to="/">{translate("UPGRADE/GO_TO_APP")}</Link>
            </Button>
          </>
        )
      }
    />
  )
}
